/* ============================================================================
 *  patientFolderPrint — the whole record, as a document you can hand over
 * ----------------------------------------------------------------------------
 *  ASKED FOR: "A report we can populate — a Word document or a PDF we can print
 *   out and give to them, with all the information of the documentation and
 *   everything, lab results and everything, arranged nicely as a professional
 *   hospital system. The patient's name and the ID and everything neatly
 *   arranged, with the vitals."
 *
 *  ── WHY PRINT-STYLED HTML AND NOT A PDF LIBRARY ────────────────────────────
 *
 *  This is the pattern ClaimsReport already uses in this codebase: open a window,
 *  write a document into it, let the browser print. It was the right choice there
 *  and it is the right choice here, for reasons that are practical rather than
 *  aesthetic:
 *
 *    · PRINT AND PDF ARE THE SAME BUTTON. Every browser's print dialogue offers
 *      "Save as PDF", so one implementation answers both halves of the request.
 *    · NO NEW DEPENDENCY. jsPDF and pdfmake ship their own fonts and layout
 *      engines — a megabyte of bundle, and a second set of typography rules to
 *      keep in step with this one.
 *    · REAL TYPOGRAPHY. A table that breaks across a page keeps its header,
 *      because that is what `thead` does when printed. Reproducing that by hand
 *      on a canvas is how printed records come to lose their column titles on
 *      page two.
 *    · IT WORKS OFFLINE, on a facility LAN, with no font fetch.
 *
 *  `docx` is already a dependency here, so a Word file is possible if somebody
 *  needs an editable one. This is a RECORD, meant to be final and handed over, so
 *  it is not the default.
 *
 *  ── WHAT MAKES IT A RECORD RATHER THAN A SCREEN DUMP ───────────────────────
 *
 *  · The patient and the claim identify themselves on every page, because a page
 *    that gets separated from the folder must still say whose it is.
 *  · Sections are ordered the way a folder is read: who, then the encounter,
 *    then what was found, then what was done, then what it cost.
 *  · A section with nothing in it SAYS SO rather than vanishing. A discharge
 *    summary with no observations section reads as though none were needed; one
 *    that says "no observations were recorded" is the truth and is exactly what
 *    a vetter is looking for.
 *  · A section that could not be READ says that instead, and does not pretend to
 *    be empty. Those are different facts.
 * ========================================================================== */

//  Every value that reaches the document goes through this. Patient names and
//  free-text notes are user data in an HTML document, and a surname containing
//  an ampersand must not be able to break the page — let alone anything worse.
const esc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

//  Free text keeps the line breaks the clinician typed. Escaped FIRST, so the
//  <br> inserted after is the only markup that survives.
const para = (v) => esc(v).replace(/\r?\n/g, '<br>');

const dmy = (v) => {
  const s = String(v == null ? '' : v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};


const yearsBetween = (dob, on) => {
  const a = String(dob || '').slice(0, 10);
  const b = String(on || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null;
  const d1 = new Date(a); const d2 = new Date(b);
  if (Number.isNaN(d1) || Number.isNaN(d2)) return null;
  let y = d2.getFullYear() - d1.getFullYear();
  const mo = d2.getMonth() - d1.getMonth();
  if (mo < 0 || (mo === 0 && d2.getDate() < d1.getDate())) y -= 1;
  return y >= 0 && y < 130 ? y : null;
};

//  A labelled pair in the identity grid.
const field = (label, value) => `
  <div class="f">
    <span class="fl">${esc(label)}</span>
    <span class="fv">${value === '' || value == null ? '—' : esc(value)}</span>
  </div>`;

//  A section, with its own "nothing here" and "could not be read" states. Those
//  are different and the document must not conflate them.
const section = (title, body, { missing = null, empty = 'Nothing recorded.' } = {}) => `
  <section class="s">
    <h2>${esc(title)}</h2>
    ${missing
    ? `<p class="unavail">This section could not be read: ${esc(missing)}</p>`
    : (body && String(body).trim() ? body : `<p class="none">${esc(empty)}</p>`)}
  </section>`;

const table = (heads, rows, { className = '' } = {}) => {
  if (!rows.length) return '';
  return `
  <table class="${className}">
    <thead><tr>${heads.map((h) => `<th>${esc(h.label || h)}</th>`).join('')}</tr></thead>
    <tbody>${rows.join('')}</tbody>
  </table>`;
};

//  ── the observation table, used for the visit and for each day of a stay ────
const vitalsTable = (readings) => {
  const rows = (readings || []).map((v) => {
    const t = Number(String(v.tem || '').replace(/[^\d.]/g, ''));
    const hot = Number.isFinite(t) && t >= 37.5;
    return `<tr>
      <td>${esc(v.time || v.at || '—')}</td>
      <td class="${hot ? 'hi' : ''}">${esc(v.tem || v.temperature || '—')}</td>
      <td>${esc(v.pulse || '—')}</td>
      <td>${esc(v.res || v.respiration || '—')}</td>
      <td>${esc(v.pre || v.bloodPressure || '—')}</td>
      <td>${esc(v.spo2 || '—')}</td>
    </tr>`;
  });
  return table(['Time', 'Temp °C', 'Pulse', 'Resp', 'BP', 'SpO₂'], rows, { className: 'num' });
};

export function folderHtml(folder, { facility } = {}) {
  const c = folder.claim || {};
  const h = c.header || {};
  const un = folder.unavailable || {};
  const notes = folder.notes || {};
  const stay = folder.stay;
  const labs = folder.labs;

  const name = [h.surname, h.other_names].filter(Boolean).join(' ').trim() || 'Unnamed patient';
  const visit = h.visit_date || h.admission_date;
  const age = yearsBetween(h.date_of_birth, visit);
  const nights = (() => {
    const a = String(h.admission_date || h.visit_date || '').slice(0, 10);
    const b = String(h.discharge_date || '').slice(0, 10);
    if (!a || !b) return null;
    const n = Math.round((new Date(b) - new Date(a)) / 86400000);
    return Number.isFinite(n) && n >= 0 ? n : null;
  })();

  // ── who ───────────────────────────────────────────────────────────────────
  const identity = `
    <div class="grid">
      ${field('Patient', name)}
      ${field('Folder number', h.folder_no)}
      ${field('Membership no.', h.member_no)}
      ${field('CCC number', h.ccc)}
      ${field('Date of birth', dmy(h.date_of_birth))}
      ${field('Age at visit', age == null ? '' : `${age} years`)}
      ${field('Sex', h.gender)}
      ${field('Dependant', h.is_dependant ? 'Yes' : 'No')}
      ${field('Insurance', h.scheme_name)}
      ${field('Policy number', h.policy_option)}
    </div>`;

  const encounter = `
    <div class="grid">
      ${field('Visit date', dmy(visit))}
      ${field('Discharge date', h.discharge_date ? dmy(h.discharge_date) : 'not discharged')}
      ${field('Attendance', h.attendance_type)}
      ${field('Length of stay', nights == null ? '' : (nights === 0 ? 'Same day' : `${nights} night${nights === 1 ? '' : 's'}`))}
      ${field('Type of attendance', h.attendance_code)}
      ${field('Service outcome', h.outcome)}
      ${field('Service billed', [h.service_name, h.service_code].filter(Boolean).join(' · '))}
      ${field('Specialty', [h.specialty_name, h.specialty_code].filter(Boolean).join(' · '))}
      ${field('Attending', h.physician_name)}
      ${field('Patient status', h.patient_status)}
    </div>`;

  // ── the encounter notes ───────────────────────────────────────────────────
  const noteBlock = [
    ['Presenting complaint', notes.complaint],
    ['History', notes.history],
    ['Physical examination', notes.exam],
    ['Other documentation', notes.other],
  ].filter(([, v]) => String(v || '').trim())
    .map(([l, v]) => `<div class="note"><h3>${esc(l)}</h3><p>${para(v)}</p></div>`)
    .join('');

  // ── what was found and done ───────────────────────────────────────────────
  const dxRows = (c.diagnoses || []).map((d) => `<tr>
      <td>${esc(d.icd10_code || '—')}</td>
      <td>${esc(d.description || '—')}</td>
      <td>${esc(d.gdrg_code || '—')}</td>
      <td class="c">${d.is_principal ? '✓' : ''}</td>
    </tr>`);
  const pxRows = (c.procedures || []).map((p) => `<tr>
      <td>${esc(p.gdrg_code || '—')}</td>
      <td>${esc(p.description || '—')}</td>
      <td>${esc(p.icd10_code || '—')}</td>
      <td>${esc(dmy(p.performed_at))}</td>
      <td class="c">${p.is_principal ? '✓' : ''}</td>
    </tr>`);
  const medRows = (c.medications || []).map((m) => `<tr>
      <td>${esc(m.generic_name || m.code || '—')}</td>
      <td>${esc(m.dose_unit || '—')}</td>
      <td>${esc(m.frequency || '—')}</td>
      <td>${esc(m.duration || '—')}</td>
      <td>${esc(m.route || '—')}</td>
      <td class="r">${esc(m.quantity || '—')}</td>
    </tr>`);
  const invRows = (c.investigations || []).map((i) => `<tr>
      <td>${esc(i.description || i.code || '—')}</td>
      <td>${esc(i.kind || '—')}</td>
      <td>${esc(i.result || '')}</td>
    </tr>`);

  // ── the stay, day by day ──────────────────────────────────────────────────
  const stayBlock = !stay ? '' : `
    <p class="lead">
      ${esc(stay.nights)} night${stay.nights === 1 ? '' : 's'},
      ${esc(dmy(stay.from))} to ${esc(dmy(stay.to))}.
      ${esc(stay.summary.rounds)} ward round${stay.summary.rounds === 1 ? '' : 's'},
      ${esc(stay.summary.vitals)} observation${stay.summary.vitals === 1 ? '' : 's'},
      ${esc(stay.summary.notes)} nursing note${stay.summary.notes === 1 ? '' : 's'}.
      ${stay.summary.daysWithoutRound > 0
    ? `<strong>${esc(stay.summary.daysWithoutRound)} day${stay.summary.daysWithoutRound === 1 ? '' : 's'} without a ward round.</strong>`
    : ''}
    </p>
    ${stay.days.map((d) => `
      <div class="day">
        <h3>Day ${esc(d.dayNumber)} &middot; ${esc(dmy(d.day))}</h3>
        ${d.rounds.length
    ? d.rounds.map((r) => `
            <div class="round">
              <div class="rh">${esc(r.state || 'Ward round')}${r.doctors && r.doctors.length ? ` — ${esc(r.doctors.join(', '))}` : ''}</div>
              ${r.complaint ? `<p><span class="k">Complaint</span> ${para(r.complaint)}</p>` : ''}
              ${r.exam ? `<p><span class="k">On examination</span> ${para(r.exam)}</p>` : ''}
              ${r.plan ? `<p><span class="k">Plan</span> ${para(r.plan)}</p>` : ''}
              ${r.other ? `<p><span class="k">Other</span> ${para(r.other)}</p>` : ''}
            </div>`).join('')
    : '<p class="none">No ward round was written on this day.</p>'}

        ${d.vitals.length
    ? `<h4>Observations</h4>${vitalsTable(d.vitals)}`
    : '<h4>Observations</h4><p class="none">No observations were recorded on this day.</p>'}

        ${d.notes.length
    ? `<h4>Nursing notes</h4>${d.notes.map((n) => `<p class="nn"><span class="t">${esc(n.at || '')}</span> ${para(n.note)}</p>`).join('')}`
    : ''}

        ${(d.medications.length || d.investigations.length)
    ? `<h4>Given and requested</h4><ul class="giv">${[
      ...d.medications.map((m) => `<li>${esc([m.name, m.dose, m.frequency, m.route].filter(Boolean).join(' · '))}</li>`),
      ...d.investigations.map((l) => `<li>${esc(l.test)}</li>`),
    ].join('')}</ul>`
    : ''}
      </div>`).join('')}`;

  // ── the laboratory ────────────────────────────────────────────────────────
  const labBlock = !labs ? '' : (labs.tests || []).map((t) => `
    <div class="lab">
      <h3>${esc(t.name || t.code)}${t.lab_no ? ` <span class="sub">specimen ${esc(t.lab_no)}</span>` : ''}</h3>
      ${t.rows && t.rows.length
    ? table(['Parameter', 'Result', 'Unit', 'Reference range', ''],
      t.rows.map((r) => `<tr>
              <td>${esc(r.parameter)}</td>
              <td class="b">${esc(r.result)}</td>
              <td>${esc(r.unit || '')}</td>
              <td class="sub">${esc(r.range || '')}</td>
              <td class="c ${/high|low|abnormal|positive/i.test(r.flag) ? 'hi' : ''}">${esc((r.flag || '').toUpperCase())}</td>
            </tr>`), { className: 'num' })
    : `<p class="none">${t.form
      ? 'No results have been entered for this test.'
      : 'No laboratory form is matched to this test, so no results can be held against it.'}</p>`}
      ${t.claim_result ? `<p class="cr"><span class="k">On the claim</span> ${para(t.claim_result)}</p>` : ''}
    </div>`).join('');

  /*  NO COST SUMMARY. Removed at the officer's instruction after seeing the first
   *  print. It was the wrong thing on this document twice over: the folder is a
   *  CLINICAL record meant to be handed to a patient or a vetter, and money on it
   *  invites a conversation about price in a document about care. On this
   *  database it also read GHS 0.00 down the column, because an imported claim's
   *  totals are derived from tariffs the claim does not carry — so it was
   *  volunteering a figure that was both out of place and wrong. */

  const title = `${name} — ${h.folder_no || h.member_no || 'folder'}`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>${esc(title)}</title>
<style>
  /*  A4 with real margins. The running head repeats on every page, because a
      page separated from the folder must still say whose it is. */
  @page { size: A4; margin: 14mm 13mm 16mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0; background: #f1f5f9;
    font: 11pt/1.5 "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #111827; -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sheet { max-width: 210mm; margin: 0 auto; background: #fff; padding: 16mm 14mm; }

  .bar {
    position: sticky; top: 0; display: flex; gap: 10px; align-items: center;
    background: #0f2b4c; color: #fff; padding: 10px 14px; font-size: 10pt;
  }
  .bar button {
    font: inherit; font-weight: 700; padding: 6px 14px; border-radius: 6px;
    border: 0; background: #16a34a; color: #fff; cursor: pointer;
  }
  .bar .hint { opacity: .85; }

  header.doc { border-bottom: 2.5px solid #0f2b4c; padding-bottom: 8px; margin-bottom: 14px; }
  header.doc .fac { font-size: 14pt; font-weight: 800; letter-spacing: .2px; }
  header.doc .kind {
    font-size: 9.5pt; font-weight: 700; text-transform: uppercase;
    letter-spacing: 1.4px; color: #475569; margin-top: 2px;
  }
  header.doc .when { float: right; font-size: 8.5pt; color: #64748b; text-align: right; }

  h2 {
    font-size: 10.5pt; font-weight: 800; text-transform: uppercase;
    letter-spacing: .8px; color: #0f2b4c; margin: 16px 0 6px;
    border-bottom: 1px solid #cbd5e1; padding-bottom: 3px;
  }
  h3 { font-size: 10.5pt; font-weight: 800; margin: 11px 0 4px; color: #1e293b; }
  h4 {
    font-size: 8.8pt; font-weight: 800; text-transform: uppercase;
    letter-spacing: .6px; color: #64748b; margin: 9px 0 3px;
  }
  p { margin: 0 0 5px; }
  .lead { font-size: 10.5pt; color: #334155; margin-bottom: 8px; }
  .none { color: #94a3b8; font-style: italic; font-size: 10pt; }
  .unavail { color: #b45309; font-size: 10pt; }
  .sub { color: #64748b; font-weight: 400; font-size: 9pt; }
  .k {
    display: inline-block; min-width: 92px; font-size: 8.5pt; font-weight: 800;
    text-transform: uppercase; letter-spacing: .5px; color: #64748b;
    vertical-align: top;
  }

  .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px 22px; }
  .f { display: flex; gap: 8px; padding: 3px 0; border-bottom: 1px dotted #e2e8f0; }
  .fl { flex: 0 0 38%; font-size: 9pt; color: #64748b; }
  .fv { flex: 1; font-weight: 600; }

  table { width: 100%; border-collapse: collapse; margin: 4px 0 8px; font-size: 9.5pt; }
  th {
    text-align: left; background: #eef2f7; color: #334155; font-size: 8.5pt;
    font-weight: 800; text-transform: uppercase; letter-spacing: .5px;
    padding: 4px 6px; border-bottom: 1px solid #cbd5e1;
  }
  td { padding: 4px 6px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
  td.r, th.r { text-align: right; }
  td.c { text-align: center; }
  td.b { font-weight: 700; }
  td.hi { color: #b91c1c; font-weight: 700; }
  table.num td { font-variant-numeric: tabular-nums; }
  tr.tot td { font-weight: 800; border-top: 1.5px solid #0f2b4c; border-bottom: 0; }

  .note { margin-bottom: 8px; }
  .day {
    border: 1px solid #dbe3ec; border-radius: 4px; padding: 8px 10px;
    margin-bottom: 9px;
  }
  .day > h3 {
    margin: -8px -10px 7px; padding: 5px 10px; background: #f6f8fb;
    border-bottom: 1px solid #dbe3ec; font-size: 10pt;
  }
  .round { margin-bottom: 7px; }
  .rh { font-size: 9pt; font-weight: 800; color: #0f2b4c; margin-bottom: 2px; }
  .nn { font-size: 10pt; }
  .nn .t { color: #64748b; font-size: 9pt; margin-right: 5px; }
  .giv { margin: 2px 0 0 16px; padding: 0; font-size: 10pt; }
  .lab { margin-bottom: 11px; }
  .cr { font-size: 10pt; margin-top: 3px; }

  footer.doc {
    margin-top: 18px; padding-top: 7px; border-top: 1px solid #cbd5e1;
    font-size: 8.5pt; color: #64748b;
  }

  /*  ── PRINT ──────────────────────────────────────────────────────────────
      The controls go, the paper is white, and nothing that belongs together is
      allowed to break across a page: a day of a stay, a laboratory panel and a
      table row each stay whole. A table that does break keeps its header, which
      is what thead does when printed and is the main reason this is a document
      rather than a canvas. */
  @media print {
    .bar { display: none !important; }
    body { background: #fff; }
    .sheet { max-width: none; margin: 0; padding: 0; }
    thead { display: table-header-group; }
    tr, .day, .lab, .note, .round { break-inside: avoid; page-break-inside: avoid; }
    h2, h3 { break-after: avoid; page-break-after: avoid; }
    section.s { break-before: auto; }
  }
</style>
</head><body>
<div class="bar">
  <button onclick="window.print()">Print / Save as PDF</button>
  <span class="hint">Choose “Save as PDF” in the print dialogue to keep a copy.</span>
</div>

<div class="sheet">
  <header class="doc">
    <div class="when">
      Generated ${esc(dmy(new Date().toISOString()))}<br>
      ${c.source === 'facility' ? 'Typed-in claim' : 'Imported claim'} #${esc(h.id || '')}
    </div>
    <div class="fac">${esc(facility || h.facility_name || 'Patient folder')}</div>
    <div class="kind">Patient folder &middot; claim record</div>
  </header>

  ${section('Patient', identity)}
  ${section('This encounter', encounter)}
  ${section('Clinical notes', noteBlock, {
    missing: un.notes,
    empty: 'No complaint, history or examination has been recorded for this visit.',
  })}
  ${section('Diagnoses',
    table(['ICD-10', 'Diagnosis', 'G-DRG', 'Principal'], dxRows),
    { empty: 'No diagnosis is recorded on this claim.' })}
  ${section('Procedures',
    table(['G-DRG', 'Procedure', 'ICD-10', 'Date', 'Principal'], pxRows),
    { empty: 'No procedure is recorded on this claim.' })}
  ${section('Observations on the visit',
    folder.vitals ? vitalsTable([folder.vitals]) : '',
    {
      missing: un.vitals,
      empty: 'No observations were recorded against this visit.',
    })}
  ${stay || un.stay
    ? section('The stay, day by day', stayBlock, {
      missing: un.stay,
      empty: 'No ward record could be matched to this claim.',
    })
    : ''}
  ${section('Laboratory and imaging', labBlock, {
    missing: un.labs,
    empty: 'No investigation is recorded on this claim.',
  })}
  ${section('Medications',
    table(['Medicine', 'Dose unit', 'Frequency', 'Days', 'Route', 'Qty'], medRows),
    { empty: 'No medication is recorded on this claim.' })}
  ${section('Investigations as billed',
    table(['Investigation', 'Kind', 'Result on the claim'], invRows),
    { empty: 'No investigation is recorded on this claim.' })}

  <footer class="doc">
    ${esc(name)} &middot; folder ${esc(h.folder_no || '—')} &middot;
    visit ${esc(dmy(visit))} &middot;
    printed from the claims record on ${esc(dmy(new Date().toISOString()))}.
    ${folder.unavailable
    ? 'One or more sections could not be read and are marked in the document.'
    : ''}
  </footer>
</div>
</body></html>`;
}

/*  Opens the folder in its own window.
 *
 *  A WINDOW RATHER THAN A DOWNLOAD. The officer nearly always wants to look at
 *  it before it goes anywhere, and a file that lands in Downloads and has to be
 *  found and opened is two steps to discover the discharge note is missing. The
 *  Print button is right there and offers Save as PDF.
 *
 *  Returns false when the browser blocked the window, so the caller can say so
 *  rather than appearing to do nothing — a popup blocker is the single most
 *  common reason a print button "does not work".
 */
export function openPatientFolder(folder, opts) {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(folderHtml(folder, opts || {}));
  w.document.close();
  return true;
}
